import { readBrowserSessionPresence } from "@/src/shared/browser/sessionStorage";

import {
  CONSENT_COOKIE_MAX_AGE_SECONDS,
  CONSENT_COOKIE_NAME,
  createConsentState,
  currentConsent,
  deviceConsentForOwner,
  effectiveConsentChoices,
  encodeConsentCookie,
  hasBrowserPrivacySignal,
  isConsentOptInRegion,
  isDefaultAnalyticsBasis,
  needsConsentDecision,
  normalizeConsentOwnerTag,
  normalizeConsentState,
  planConsentSync,
  readConsentFromCookieHeader,
  shouldLoadTelemetry,
  type BrowserPrivacySignalSource,
  type ConsentCategory,
  type ConsentChoices,
  type ConsentState,
  type DeviceConsentState,
  type TelemetryComplianceMode,
  withConsentOwner,
} from "./policy";

/**
 * Browser side of tracker consent: the `sm_consent` cookie, the EU/UK opt-in
 * region check, change events, the `openConsentPreferences()` hook and the
 * best-effort account sync. Every telemetry loader asks
 * `isTelemetryAllowedInBrowser` before it starts.
 */

export const CONSENT_CHANGED_EVENT = "smartmanager:consent-changed";
export const OPEN_CONSENT_PREFERENCES_EVENT = "smartmanager:open-consent-preferences";
/** Pre-consent per-device PostHog flag ("enabled" | "disabled"). Migrated, then removed. */
export const LEGACY_ANALYTICS_PREFERENCE_KEY = "smartmanager_product_analytics_v1";

/** Device hint that this browser last hydrated a HIPAA-mode account. */
export const HIPAA_DEVICE_HINT_KEY = "smartmanager_hipaa_device_v1";

// The signed-in account's compliance mode, set from the verified session on
// every hydration. HIPAA mode denies every non-necessary category regardless
// of consent, through the same `shouldLoadTelemetry` decision. Until the
// verified session arrives, a device that last hydrated a HIPAA account is
// treated as HIPAA (the hint in localStorage), so Axiom and the tracker gate
// send nothing on the next page load. The hint can only restrict: only a
// verified standard session clears it.
let verifiedComplianceMode: TelemetryComplianceMode | null = null;

// Signed-in devices hold the regional default back until the account has been
// read: its compliance mode (a HIPAA account must never start a tracker) and
// its consent copy (an account opt-out must win over the device default).
// Until both arrive only an explicit device choice counts. A failed read keeps
// the default held for the rest of the page load, so failures cost analytics,
// never consent. A stale "signed in" hint does the same on public pages.
let accountComplianceVerified = false;
let accountConsentSettled = false;
let accountReconcile: Promise<void> | null = null;

// The signed-in account's opaque consent owner tag, from the account read (or
// a save's answer) on this page load. The `sm_consent` cookie is per browser
// and outlives sign-out, so a signed-in device counts a tagged choice only
// when its tag is this one: another account's choice never decides this
// account's telemetry and never reaches its account copy. Until the tag is
// known, a tagged choice counts for nobody (nothing runs on it).
let accountOwnerTag: string | null = null;

// Unreadable session storage counts as signed in, so the hold stays on
// (fails closed). The account read then fails or answers, as for any session.
function browserSignedIn(): boolean {
  return readBrowserSessionPresence() !== "absent";
}

/** True while a signed-in device waits for its account before any default applies. */
export function browserConsentDefaultsPending(): boolean {
  // State is per page load. Signing in goes through the OAuth redirect, a
  // full page load, so a new account never inherits an earlier account's read.
  return browserSignedIn() && (!accountComplianceVerified || !accountConsentSettled);
}

function notifyConsentChanged(): void {
  if (hasWindow()) window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: readBrowserConsent() }));
}

function readHipaaDeviceHint(): boolean {
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem(HIPAA_DEVICE_HINT_KEY) === "hipaa";
  } catch {
    return false;
  }
}

function writeHipaaDeviceHint(mode: TelemetryComplianceMode): void {
  if (!hasWindow()) return;
  try {
    if (mode === "hipaa") window.localStorage.setItem(HIPAA_DEVICE_HINT_KEY, "hipaa");
    else window.localStorage.removeItem(HIPAA_DEVICE_HINT_KEY);
  } catch {
    // Storage can be unavailable (private mode); the verified session still applies.
  }
}

export function getBrowserTelemetryComplianceMode(): TelemetryComplianceMode {
  return verifiedComplianceMode ?? (readHipaaDeviceHint() ? "hipaa" : "standard");
}

/**
 * Applies the verified account mode and re-runs every telemetry loader. True
 * when what may run changed: the mode, or the end of the signed-in wait.
 */
export function setBrowserTelemetryComplianceMode(mode: unknown): boolean {
  const next: TelemetryComplianceMode = mode === "hipaa" ? "hipaa" : "standard";
  const previous = getBrowserTelemetryComplianceMode();
  const wasPending = browserConsentDefaultsPending();
  verifiedComplianceMode = next;
  accountComplianceVerified = true;
  writeHipaaDeviceHint(next);
  // A sign-in without a page load (or a first hydration) still reads the account copy once.
  if (!accountConsentSettled) void reconcileConsentWithAccount();
  const changed = next !== previous || wasPending !== browserConsentDefaultsPending();
  if (changed) notifyConsentChanged();
  return changed;
}

/** Test seam: forget the verified mode and the account sync, as a fresh page load would. */
export function resetBrowserTelemetryComplianceModeForTests(): void {
  verifiedComplianceMode = null;
  accountComplianceVerified = false;
  accountConsentSettled = false;
  accountReconcile = null;
  accountOwnerTag = null;
}

function hasWindow(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function browserPrivacySignal(): boolean {
  if (typeof navigator === "undefined") return false;
  return hasBrowserPrivacySignal(navigator as BrowserPrivacySignalSource);
}

// Whether this device gets opt-in consent (EU/EEA/UK signal, or no usable
// timezone). Read once per page load from the browser's own timezone and
// languages; no network and no IP lookup. Server rendering has no browser
// signals and treats every request as opt-in, so nothing runs before mount.
let cachedOptInRegion: boolean | null = null;
let optInRegionOverrideForTests: boolean | null = null;

function detectBrowserOptInRegion(): boolean {
  if (!hasWindow() || typeof navigator === "undefined") return true;
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const languages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];
  return isConsentOptInRegion({ timeZone, languages });
}

/** True when trackers on this device wait for an explicit opt-in (EU/EEA/UK rules). */
export function browserConsentOptInRegion(): boolean {
  if (optInRegionOverrideForTests !== null) return optInRegionOverrideForTests;
  if (!hasWindow()) return true;
  if (cachedOptInRegion === null) cachedOptInRegion = detectBrowserOptInRegion();
  return cachedOptInRegion;
}

/** Test seam: pin the region result (`null` re-detects from the mocked browser). */
export function setBrowserConsentRegionForTests(optInRegion: boolean | null): void {
  optInRegionOverrideForTests = optInRegion;
  cachedOptInRegion = null;
}

/** The raw `sm_consent` cookie, whoever it belongs to. */
function readStoredBrowserConsent(): DeviceConsentState | null {
  if (!hasWindow()) return null;
  try {
    return readConsentFromCookieHeader(document.cookie);
  } catch {
    return null;
  }
}

/**
 * The choice that applies to whoever uses this browser now. Signed out, that
 * is the device's choice. Signed in, a choice tagged for another account (or
 * for an account not yet confirmed as this one) reads as no choice.
 */
export function readBrowserConsent(): DeviceConsentState | null {
  const stored = readStoredBrowserConsent();
  return browserSignedIn() ? deviceConsentForOwner(stored, accountOwnerTag) : stored;
}

/**
 * True while a signed-in device holds a tagged choice and the account read has
 * not yet said whose it is. The banner waits rather than asking someone whose
 * choice may already be stored; nothing runs on it meanwhile. A settled read
 * with no tag leaves the choice someone else's, so the banner asks.
 */
function storedConsentOwnerUnconfirmed(): boolean {
  return (
    browserSignedIn() &&
    accountOwnerTag === null &&
    !accountConsentSettled &&
    readStoredBrowserConsent()?.owner !== undefined
  );
}

/** Records the verified owner tag. True when it changed what this device reads. */
function setAccountOwnerTag(value: unknown): boolean {
  const tag = normalizeConsentOwnerTag(value);
  if (!tag || tag === accountOwnerTag) return false;
  accountOwnerTag = tag;
  return readStoredBrowserConsent()?.owner !== undefined;
}

export function isTelemetryAllowedInBrowser(
  category: ConsentCategory,
  complianceMode?: TelemetryComplianceMode | null,
): boolean {
  // Either the caller's mode or the account's mode can impose HIPAA; neither can lift it.
  const effectiveMode: TelemetryComplianceMode = complianceMode === "hipaa" ? "hipaa" : getBrowserTelemetryComplianceMode();
  return shouldLoadTelemetry({
    category,
    consent: readBrowserConsent(),
    gpc: browserPrivacySignal(),
    complianceMode: effectiveMode,
    optInRegion: browserConsentOptInRegion(),
    defaultsApply: !browserConsentDefaultsPending(),
  });
}

/**
 * The choices in force on this device before privacy signals and HIPAA mode:
 * the stored current-version choice, or the regional default. Used to fill
 * the preferences dialog and Settings, so it shows the default even during the
 * short signed-in wait in which no default runs yet.
 */
export function browserEffectiveConsentChoices(consent: DeviceConsentState | null = readBrowserConsent()): ConsentChoices {
  return effectiveConsentChoices(consent, browserConsentOptInRegion());
}

/** Drops the query string and fragment (OAuth `code`/`state`, tokens, search terms) from an event URL. */
export function stripUrlQueryAndHash(url: string): string {
  const cut = url.search(/[?#]/);
  return cut < 0 ? url : url.slice(0, cut);
}

/**
 * A `beforeSend` hook for trackers that cannot be torn down once injected
 * (Vercel Analytics, Speed Insights): consent is re-read on every event, so a
 * withdrawal stops sending immediately rather than at the next reload. Events
 * that pass keep only the page path: the query string and fragment are cut.
 */
export function dropEventUnlessAllowed(category: ConsentCategory, complianceMode?: TelemetryComplianceMode | null) {
  return <T>(event: T): T | null => {
    if (!isTelemetryAllowedInBrowser(category, complianceMode)) return null;
    if (!event || typeof event !== "object") return event;
    const url = (event as { url?: unknown }).url;
    return typeof url === "string" ? { ...event, url: stripUrlQueryAndHash(url) } : event;
  };
}

export function browserNeedsConsentDecision(): boolean {
  if (storedConsentOwnerUnconfirmed()) return false;
  return needsConsentDecision(readBrowserConsent(), {
    gpc: browserPrivacySignal(),
    optInRegion: browserConsentOptInRegion(),
  });
}

function writeCookie(state: DeviceConsentState): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeConsentCookie(state)}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function removeLegacyPreference(): void {
  try {
    window.localStorage.removeItem(LEGACY_ANALYTICS_PREFERENCE_KEY);
  } catch {
    // Storage can be unavailable (private mode); the cookie is authoritative.
  }
}

/** Stores a state on this device and tells every loader. No network. */
export function applyBrowserConsent(state: DeviceConsentState): void {
  if (!hasWindow()) return;
  writeCookie(state);
  removeLegacyPreference();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: state }));
}

export type ConsentSource = "banner" | "preferences" | "settings";

export type SaveConsentOptions = {
  /**
   * False when the person did not set the analytics switch themselves (they
   * saved the preferences dialog as shown). An "on" is then kept as the
   * regional default, never as consent, and never becomes the account copy.
   */
  analyticsAffirmed?: boolean;
};

/**
 * Records a person's choice: cookie first (so loaders react at once), then the
 * account copy and receipt when signed in. Returns the stored state.
 */
export function saveConsentChoices(
  choices: ConsentChoices,
  source: ConsentSource,
  { analyticsAffirmed = true }: SaveConsentOptions = {},
): DeviceConsentState | null {
  if (!hasWindow()) return null;
  const previous = readBrowserConsent();
  // Signed in, the choice is this account's from the start. Signed out it is
  // anonymous; if the account is not known yet, the save's answer tags it.
  const owner = browserSignedIn() ? accountOwnerTag : null;
  const state = withConsentOwner(
    createConsentState(choices, { gpc: browserPrivacySignal(), analyticsAffirmed }),
    owner,
  );
  applyBrowserConsent(state);
  void syncConsentToAccount(state, source, isDefaultAnalyticsBasis(previous) ? previous?.decidedAt : undefined);
  return state;
}

/**
 * `previousDefaultAt` is the time of the device default this save replaces.
 * The server uses it to recognise its own earlier account copy of that
 * default, which it may overwrite; it never overwrites a real decision.
 */
async function syncConsentToAccount(
  state: DeviceConsentState,
  source: ConsentSource,
  previousDefaultAt?: number,
): Promise<void> {
  if (!browserSignedIn()) return;
  const unaffirmed = isDefaultAnalyticsBasis(state);
  try {
    const { authenticatedJsonRequest } = await import("@/src/client/http/authenticatedJsonRequest");
    const response = await authenticatedJsonRequest<{ ownerTag?: unknown } | null>({
      endpoint: "privacy/consent",
      method: "POST",
      // The server records the receipt with this basis and stores the
      // diagnostics choice with analytics off, never the default "on".
      // The owner tag is device-only; the server derives its own.
      body: {
        consent: withConsentOwner(state, null),
        source,
        ...(unaffirmed ? { analyticsBasis: "default" } : {}),
        ...(unaffirmed && previousDefaultAt !== undefined ? { previousDefaultAt } : {}),
      },
      keepalive: true,
      timeoutMs: 15_000,
    });
    tagSavedConsent(state, response?.ownerTag);
  } catch (error) {
    // The device choice already applies; the account copy catches up on the
    // next reconcile. A refusal (the demo, or a silo awaiting its update)
    // still names the account, so the choice is bound to it all the same.
    tagSavedConsent(state, ownerTagFromError(error));
  }
}

function ownerTagFromError(error: unknown): unknown {
  const body = (error as { body?: unknown } | null)?.body;
  return body && typeof body === "object" ? (body as { ownerTag?: unknown }).ownerTag : undefined;
}

/**
 * Binds a choice saved before the account was known to the account the save
 * reached, unless the device has stored another choice since.
 */
function tagSavedConsent(saved: DeviceConsentState, ownerTag: unknown): void {
  const owner = normalizeConsentOwnerTag(ownerTag);
  if (!owner || !browserSignedIn()) return;
  if (accountOwnerTag === null) accountOwnerTag = owner;
  if (owner !== accountOwnerTag) return;
  const stored = readStoredBrowserConsent();
  if (!stored || stored.owner !== undefined || stored.decidedAt !== saved.decidedAt) return;
  applyBrowserConsent(withConsentOwner(stored, owner));
}

type AccountConsentResponse = {
  consent?: unknown;
  /** Opaque tag of the signed-in account, bound to the device choice it owns. */
  ownerTag?: unknown;
  accountStorage?: boolean;
  unavailable?: boolean;
  complianceMode?: unknown;
};

async function readAndReconcileAccountConsent(): Promise<void> {
  let response: AccountConsentResponse | null;
  try {
    const { authenticatedJsonRequest } = await import("@/src/client/http/authenticatedJsonRequest");
    response = await authenticatedJsonRequest<AccountConsentResponse>({
      endpoint: "privacy/consent",
      method: "GET",
      timeoutMs: 15_000,
    });
  } catch {
    // Unreadable account: no default runs on this page load (fails closed).
    return;
  }
  // Public pages never hydrate the session, so the account mode arrives here.
  if (response?.complianceMode === "hipaa" || response?.complianceMode === "standard") {
    setBrowserTelemetryComplianceMode(response.complianceMode);
  }
  // Whose choice the cookie holds is known from here on; tell every reader.
  const ownerChanged = setAccountOwnerTag(response?.ownerTag);
  if (response?.accountStorage === true) {
    const plan = planConsentSync(
      readStoredBrowserConsent(),
      normalizeConsentState(response.consent),
      accountOwnerTag,
    );
    // Adopt before the wait ends, so an account opt-out lands before any default could start.
    if (plan.adopt) applyBrowserConsent(plan.adopt);
    if (plan.push) void syncConsentToAccount(plan.push, "preferences");
  } else if (response?.accountStorage !== false) {
    // A read failure (`unavailable`) or an unexpected answer: keep the default held.
    if (ownerChanged) notifyConsentChanged();
    return;
  }
  // `accountStorage: false` is an older silo (or the demo) with no account
  // copy: this account's device choice stands; another account's does not.
  // An anonymous choice it keeps becomes this account's, as a push would make it.
  const stored = readStoredBrowserConsent();
  if (accountOwnerTag !== null && stored && stored.owner === undefined && currentConsent(stored)) {
    applyBrowserConsent(withConsentOwner(stored, accountOwnerTag));
  }
  const wasPending = browserConsentDefaultsPending();
  accountConsentSettled = true;
  if (ownerChanged || wasPending !== browserConsentDefaultsPending()) notifyConsentChanged();
}

/**
 * Signed-in devices read the account once per page load: they adopt its
 * compliance mode and its newer choice, and push a newer device decision.
 * Until this settles (and the compliance mode is verified), no regional
 * default runs. Concurrent callers share one request.
 */
export function reconcileConsentWithAccount(): Promise<void> {
  if (!hasWindow() || !browserSignedIn() || accountConsentSettled) return Promise.resolve();
  if (!accountReconcile) {
    accountReconcile = readAndReconcileAccountConsent().finally(() => {
      accountReconcile = null;
    });
  }
  return accountReconcile;
}

/**
 * One-time migration of the old per-device PostHog flag. A stored opt-out is a
 * decision and becomes "reject all". A stored opt-in covered PostHog only, not
 * the wider analytics category, so it is kept as the Customize default and the
 * person still chooses where the banner asks (EU/UK); elsewhere analytics is
 * already on by default.
 */
export function migrateLegacyAnalyticsPreference(): { suggestAnalytics: boolean } {
  if (!hasWindow()) return { suggestAnalytics: false };
  let legacy: string | null = null;
  try {
    legacy = window.localStorage.getItem(LEGACY_ANALYTICS_PREFERENCE_KEY);
  } catch {
    return { suggestAnalytics: false };
  }
  if (readStoredBrowserConsent()) {
    if (legacy !== null) removeLegacyPreference();
    return { suggestAnalytics: false };
  }
  if (legacy === "disabled") {
    applyBrowserConsent(createConsentState({ analytics: false, diagnostics: false }, { gpc: browserPrivacySignal() }));
    return { suggestAnalytics: false };
  }
  return { suggestAnalytics: legacy === "enabled" };
}

export function subscribeToConsent(listener: (state: ConsentState | null) => void): () => void {
  if (!hasWindow()) return () => {};
  const handler = () => listener(readBrowserConsent());
  window.addEventListener(CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
}

/** Opens the consent choices in the consent bar's place, from anywhere (the cookie page's button). Settings shows them inline instead. */
export function openConsentPreferences(): void {
  if (!hasWindow()) return;
  window.dispatchEvent(new Event(OPEN_CONSENT_PREFERENCES_EVENT));
}

export function subscribeToConsentPreferencesRequests(listener: () => void): () => void {
  if (!hasWindow()) return () => {};
  window.addEventListener(OPEN_CONSENT_PREFERENCES_EVENT, listener);
  return () => window.removeEventListener(OPEN_CONSENT_PREFERENCES_EVENT, listener);
}
